import { useEffect, useState, useCallback } from "react";
import { MapPin, Users, GraduationCap, DollarSign, RefreshCw, Briefcase } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useCorporate } from "./CorporateLayout";

export default function CorporateOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/corporate/dashboard")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const corp = data?.corporate || {};
  const totals = data?.totals || {};
  const { corp: me } = useCorporate();
  const isSchoolGroup = me?.kind === "school";
  const locSub = isSchoolGroup
    ? `${totals.schools || 0} school${(totals.schools || 0) === 1 ? "" : "s"} · ${totals.offices || 0} office${(totals.offices || 0) === 1 ? "" : "s"}`
    : `${totals.locations || 0} active location${(totals.locations || 0) === 1 ? "" : "s"}`;
  const locations = data?.locations || [];
  const renewsAt = corp.paid_until ? new Date(corp.paid_until) : (corp.trial_ends_at ? new Date(corp.trial_ends_at) : null);

  return (
    <main className="px-6 md:px-10 py-8" data-testid="corporate-overview">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Across all locations</h1>
          <p className="text-sm text-slate-600">Live numbers from every campus and office under {corp.name || "your district"}.</p>
        </div>
        <button onClick={load} data-testid="corporate-refresh-btn" title="Refresh" className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-100 transition">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </header>

      <section className={`grid grid-cols-2 ${isSchoolGroup ? "md:grid-cols-4" : "md:grid-cols-3"} gap-4 mb-8`} data-testid="corporate-stats">
        <StatCard Icon={MapPin} label="Locations" value={totals.locations || 0} sub={locSub} />
        <StatCard Icon={Users} label="Visitors inside now" value={totals.visitors_inside || 0} sub={`${totals.todays_visitors || 0} signed in today`} accent="primary" />
        {isSchoolGroup && <StatCard Icon={GraduationCap} label="Students on premises" value={totals.students_on_premises || 0} sub={`${totals.todays_student_events || 0} events today`} accent="emerald" />}
        <StatCard Icon={DollarSign} label="Monthly plan" value={corp.custom_monthly_price_usd > 0 ? `$${corp.custom_monthly_price_usd.toFixed(0)}` : "Default"} sub={renewsAt ? `Renews ${renewsAt.toLocaleDateString()}` : "—"} accent="amber" />
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="corporate-overview-glance">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="font-display text-base font-semibold text-slate-900">Locations at a glance</div>
          <div className="text-xs text-slate-500">Full management on the Locations page.</div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-6 py-3">Name</th>
              <th className="px-6 py-3">Type</th>
              <th className="px-6 py-3">Inside now</th>
              <th className="px-6 py-3">Today</th>
            </tr>
          </thead>
          <tbody>
            {locations.length === 0 && !loading && (
              <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-500">No locations yet — add one on the Locations page.</td></tr>
            )}
            {locations.map((l) => {
              const isSchool = l.type === "school";
              return (
                <tr key={l.company_id} className="border-t border-slate-100" data-testid={`overview-location-${l.company_id}`}>
                  <td className="px-6 py-3 font-semibold text-slate-900">{l.company_name}</td>
                  <td className="px-6 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${
                      isSchool ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"
                    }`}>
                      {isSchool ? <GraduationCap className="h-3 w-3" /> : <Briefcase className="h-3 w-3" />}
                      {isSchool ? "School" : l.type === "care" ? "Care home" : "Office"}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-slate-700">
                    {isSchool
                      ? <><b>{l.students_on_premises}</b> student{l.students_on_premises === 1 ? "" : "s"} · {l.visitors_inside} visitor{l.visitors_inside === 1 ? "" : "s"}</>
                      : <><b>{l.visitors_inside}</b> visitor{l.visitors_inside === 1 ? "" : "s"}</>}
                  </td>
                  <td className="px-6 py-3 text-slate-700">
                    {isSchool
                      ? <><b>{l.todays_student_events}</b> student event{l.todays_student_events === 1 ? "" : "s"}</>
                      : <><b>{l.todays_visitors}</b> check-in{l.todays_visitors === 1 ? "" : "s"}</>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </main>
  );
}

function StatCard({ Icon, label, value, sub, accent }) {
  const ring = {
    primary: "ring-primary/20 bg-primary/5",
    amber: "ring-amber-200 bg-amber-50",
    emerald: "ring-emerald-200 bg-emerald-50",
  }[accent] || "ring-slate-200 bg-white";
  return (
    <div className={`rounded-2xl border border-slate-200 p-5 ${ring} ring-1`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <Icon className="h-5 w-5 text-slate-400" />
      </div>
      <div className="mt-3 font-display text-4xl font-bold tracking-tight text-slate-900">{value}</div>
      {sub && <div className="text-sm text-slate-600 mt-1.5">{sub}</div>}
    </div>
  );
}
